import * as React from "react";
import { Loader } from "lib/components/Loader";
import { Button, Modal, Form, Row, Col } from "react-bootstrap";
import I18n from "lib/I18n";
import { HelpButton as HelpButtonBase } from "lib/components/HelpButton";
import style from "./style.module.css";
import { observer } from "mobx-react-lite";
import { getStore } from "../../store";
import { Suggestion, SuggestionEntry } from "../../store/Suggestion";
import { useEffect, useState } from "react";

interface HelpButtonProps {
  limit: number;
  env: string;
}

const HelpButton = ({ limit, env }: HelpButtonProps) => (
  <HelpButtonBase>
    <ul>
      <li>
        直近{limit}件の{env}環境の投稿データをもとに候補を生成しています。
      </li>
      <li>
        データ型のサイズの選択は、範囲の中で最も大きい投稿データに合わせていています。(SMALLINT,
        INTEGER, BIGINT, VARCHARなど)
      </li>
      <li>
        定義済みのキーに関しては、直近{limit}
        件とデータ型が一致していなくても下記の候補には表示していません。
      </li>
    </ul>
  </HelpButtonBase>
);

interface SuggestionRowProps {
  suggestion: SuggestionEntry;
  depth: number;
}

const SuggestionRow = observer((props: SuggestionRowProps) => {
  const { suggestion, depth } = props;
  return (
    <div className={depth > 0 ? "ms-4" : ""}>
      <Row className="mb-2">
        <Col xs={4}>
          <Form.Control
            size="sm"
            type="text"
            placeholder={I18n.t("event_master_editor.label.key")}
            value={suggestion.key}
            readOnly
          />
        </Col>
        <Col xs={3}>
          <Form.Control
            size="sm"
            type="text"
            placeholder={I18n.t("event_master_editor.label.type")}
            value={suggestion.type}
            readOnly
          />
        </Col>
        <Col xs="auto">
          <Form.Check
            className="mt-1"
            type="checkbox"
            label={I18n.t("event_master_editor.label.repeated")}
            checked={suggestion.repeated}
            readOnly
            disabled
          />
        </Col>
      </Row>
      {suggestion.type == "RECORD" ? (
        <div>
          {suggestion.fields.map((field, i) => (
            <SuggestionRow suggestion={field} key={i} depth={depth + 1} />
          ))}
        </div>
      ) : undefined}
    </div>
  );
});

export const SuggestionDialog = observer(() => {
  const { list } = getStore();
  const [shouldGetSuggestion, setShouldGetSuggestion] = useState(true);

  const onClickClose = () => {
    list.suggestion.isOpened = false;
    setShouldGetSuggestion(true);
  };

  const onClickApply = () => {
    list.applySuggestion();
    setShouldGetSuggestion(true);
  };

  const onLimitChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    list.suggestion.limit = parseInt(e.currentTarget.value);
  };

  const onEnvChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    list.suggestion.env = e.currentTarget.value;
  };

  const onGenerateSuggestion = () => {
    list.getSuggestion();
  };

  useEffect(() => {
    if (list.suggestion.isOpened && shouldGetSuggestion) {
      setShouldGetSuggestion(false);
      list.getSuggestion();
    }
  }, [list.suggestion.isOpened, shouldGetSuggestion]);

  const renderTree = (suggestion: Suggestion) => {
    return (
      <>
        {suggestion.suggestions.map((suggestion, i) => (
          <SuggestionRow key={i} suggestion={suggestion} depth={0} />
        ))}
      </>
    );
  };

  const renderBody = (suggestion: Suggestion) => {
    let body = renderTree(suggestion);
    if (suggestion.suggestions.length === 0 && !list.suggestion.isLoading) {
      body = <div>{I18n.t("event_master_editor.label.all_keys_defined")}</div>;
    }
    return (
      <div>
        <div className={style.form}>
          <Form.Group controlId="suggestionLimit" className={style.limit}>
            <Form.Label>
              {I18n.t("event_master_editor.label.suggestion_limit")}
            </Form.Label>
            <Form.Control
              type="text"
              value={list.suggestion.limit}
              onChange={onLimitChange}
            />
          </Form.Group>
          <Form.Group controlId="suggestionEnv" className={style.env}>
            <Form.Label>
              {I18n.t("event_master_editor.label.suggestion_env")}
            </Form.Label>
            <Form.Select value={list.suggestion.env} onChange={onEnvChange}>
              {["dd1", "jd1"].map((env, i) => (
                <option key={i} value={env}>
                  {env}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <div className={style.buttons}>
            <Button onClick={onGenerateSuggestion}>
              {I18n.t("event_master_editor.action.regenerate")}
            </Button>
          </div>
        </div>
        {body}
      </div>
    );
  };

  const rowSize = list.suggestion.suggestions.length;
  const title = (
    <div>
      {I18n.t("event_master_editor.action.suggestion")}
      <div className="float-end">
        <HelpButton limit={list.suggestion.limit} env={list.suggestion.env} />
      </div>
    </div>
  );
  const body = renderBody(list.suggestion);
  const footer = (
    <div>
      <Button className="button-close-suggestion me-2" onClick={onClickClose}>
        {I18n.t("event_master_editor.action.close")}
      </Button>
      <Button
        variant="primary"
        className="button-apply-suggestion"
        onClick={onClickApply}
        disabled={rowSize === 0}
      >
        {I18n.t("event_master_editor.action.apply")}
      </Button>
    </div>
  );

  return (
    <div>
      <Modal
        animation={false}
        size="lg"
        show={list.suggestion.isOpened}
        onHide={onClickClose}
      >
        <Modal.Header>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ overflow: "auto", height: "800px" }}>
          <Loader show={list.suggestion.isLoading} style={{ height: "100%" }}>
            {body}
          </Loader>
        </Modal.Body>
        <Modal.Footer>{footer}</Modal.Footer>
      </Modal>
    </div>
  );
});
